import { useMemo } from 'react';
import { CUT_ADDONS, serviceRowToCutAddon, totalTargetSec, type CutAddon } from '@busy/core';
import { useClientVisits, useCurrentUserProfile, useCustomServices } from '@busy/api';
import { ModalCloseButton, ModalShell } from '@busy/ui';
import { useCutTimer } from '../../store/cutTimer';
import { useT } from '../../lib/i18n';
import { aggregateClientTopPicks, matchesTopPick } from '../../lib/clientTopPicks';

// Mid-cut addon picker. Filters out addons already added to this cut.
// Pick one → its phases get appended to the active cut, ticker keeps running.
export function AddonPicker({ onClose }: { onClose: () => void }) {
  const addedAddons = useCutTimer((s) => s.addedAddons);
  const addAddon = useCutTimer((s) => s.addAddon);
  const activeClient = useCutTimer((s) => s.client);
  const customServices = useCustomServices();
  const profile = useCurrentUserProfile();
  // Per-client history for the "Most Requested" crown. Skipped on
  // walk-ins (kind != 'existing'); empty visits array → no crown.
  const clientId = activeClient?.kind === 'existing' ? activeClient.id : null;
  const clientVisits = useClientVisits(clientId ?? '');
  const t = useT();

  // Source-of-truth rule (mirrors CutTypePicker): once cut_addons_seeded
  // flips true, the customs list IS the add-on menu — built-in CUT_ADDONS
  // are no longer appended, so any deletion in Settings → Cut Timer
  // sticks. Pre-seed (brand-new account that hasn't opened the editor
  // yet) we fall back to CUT_ADDONS so cold-starters still have options.
  const available = useMemo(() => {
    const taken = new Set(addedAddons.map((a) => a.id));
    const customs: CutAddon[] = (customServices.data ?? [])
      .filter((s) => s.is_addon)
      .map(serviceRowToCutAddon);
    const list = profile.data?.cut_addons_seeded
      ? customs
      : [...customs, ...CUT_ADDONS];
    return list.filter((a) => !taken.has(a.id));
  }, [addedAddons, customServices.data, profile.data?.cut_addons_seeded]);

  const topAddon = useMemo(
    () => (clientId ? aggregateClientTopPicks(clientVisits.data ?? []).addon : null),
    [clientId, clientVisits.data],
  );

  function pick(addon: CutAddon) {
    addAddon(addon);
    onClose();
  }

  return (
    <ModalShell open onClose={onClose} size="md" ariaLabel={t('cut.addonPicker.aria')}>
      <ModalCloseButton onClose={onClose} />
      <div className="border-b border-line p-4">
        <div className="text-[11px] tracking-[0.18em] uppercase font-bold text-green-700 mb-1">
          {t('cut.addonPicker.eyebrow')}
        </div>
        <h2 className="font-display text-2xl">{t('cut.addonPicker.title')}</h2>
        <p className="text-sm text-ink-500 mt-1">{t('cut.addonPicker.body')}</p>
      </div>
      <div className="p-4 space-y-2">
        {available.length === 0 && (
          <div className="rounded-lg border border-dashed border-line p-6 text-center text-ink-500 text-sm">
            {t('cut.addonPicker.empty')}
          </div>
        )}
        {available.map((a) => {
          const totalMin = Math.round(totalTargetSec(a.phases as never[]) / 60);
          const phasesLine = a.phases.map((p) => (p.nameKey ? t(p.nameKey) : p.name)).join(' · ');
          const addonName = a.nameKey ? t(a.nameKey) : a.name;
          // Crown when this client's most-requested add-on (2+ visits)
          // matches this catalog row. Same matching policy as the
          // service picker — service_id first, normalized name fallback.
          const isTopRequested = matchesTopPick(topAddon, {
            id: a.id,
            name: a.name,
            resolvedName: addonName,
          });
          return (
            <button
              key={a.id}
              type="button"
              onClick={() => pick(a)}
              className={`w-full text-left rounded-xl border bg-surface p-3 flex items-center gap-3 transition-colors ${
                isTopRequested
                  ? 'border-gold-500 ring-1 ring-gold-500/40 hover:border-gold-400'
                  : 'border-line hover:border-green-600'
              }`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <strong>{addonName}</strong>
                  {isTopRequested && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-gold-500 text-navy-900 text-[10px] font-extrabold uppercase tracking-wide">
                      <CrownIcon className="w-2.5 h-2.5" />
                      <span>{t('cut.picker.mostRequested')}</span>
                    </span>
                  )}
                  <span className="ml-auto bg-green-50 dark:bg-green-600/20 text-green-600 dark:text-green-300 text-[11px] font-extrabold px-2 py-0.5 rounded-full">
                    +${(a.priceCents / 100).toFixed(0)}
                  </span>
                </div>
                <div className="text-xs text-ink-500 mt-0.5">
                  {t('cut.addonPicker.duration', { min: totalMin, phases: phasesLine })}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </ModalShell>
  );
}

function CrownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="1" strokeLinejoin="round" aria-hidden>
      <path d="M2 19h20l-2 2H4l-2-2z" />
      <path d="M3 7l4 5 5-8 5 8 4-5v10H3V7z" />
    </svg>
  );
}
